import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { getDefaultRequiredValue } from "components/Form/components/SchemaForm/SchemaFormService";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { useDragAndDropList } from "containers/DragAndDrop/useDragAndDropList";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { ResourceWrapper } from "containers/Resources/screens/ResourcesSingle/components/ResourceWrapper/ResourceWrapper";
import { valueAndSchema } from "containers/Resources/screens/ResourcesSingle/constants";
import { newRecordId } from "containers/Resources/screens/ResourcesSingle/utils";
import { Record, Resource_Wrapper, Schedule, Wrapper } from "graphql/generated/graphql";
import { cloneDeep, isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMount } from "react-use";
import { getTypedLowerCase } from "utils/service";
import { getIri } from "utils/utils";
import { useResourceData } from "../../useResourceData";
import { AddWrapperComponent } from "./AddWrapperComponent";
import styles from "./ResourceWrapper.module.scss";
import { actions as wrapperListActions } from "./WrapperList.reducer";
import { getResourceWrapperList, getWrappers } from "./WrapperList.selectors";

interface ResourceWrapperContainerProps {
    setNestedFormSaveData?: (value: any) => void;
    nestedFormSaveData?: { [key: string]: () => void };
    isNewRecord?: boolean;
}

export const ResourceWrapperContainer = ({
    setNestedFormSaveData,
    nestedFormSaveData,
    isNewRecord = false,
}: ResourceWrapperContainerProps) => {
    const dispatch = useDispatch();

    const [isLoading, setLoading] = useState(false);

    const resourceData = useResourceData().resourceData;
    const { isDisabled, isReadOnly } = useResourceData();

    const fetchResourceWrappers = useCallback(
        () =>
            new Promise((resolve, reject) => {
                setLoading(true);

                const filterStr = JSON.stringify({
                    field: "resource_id",
                    op: "eq",
                    value: resourceData?.id,
                });
                dispatch({
                    promise: { resolve, reject },
                    type: wrapperListActions.FETCH_RESOURCE_WRAPPER_LIST,
                    params: {
                        query: `
                            query resource_wrapper_plain_list($filter: String) {
                                resource_wrapper_plain_list(filter: $filter, offset: 0, order: "+id", limit: 20)  {
                                id
                                order
                                wrapper_id_wrapper {
                                    id
                                    name
                                    input_schema
                                }
                            }
                        }
                            
                    `,
                        variables: {
                            filter: filterStr,
                        },
                    },
                    resourceId: resourceData?.id,
                });
            })
                .then((data: any) => {
                    const count = data?.resource_wrapper_plain_list?.length;
                    dispatch({
                        type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                        iri: getIri(resourceData?.record_type, resourceData?.id),
                        response: {
                            resource_wrapper_list_resource_id: {
                                resource_wrapper_count: count,
                            },
                        },
                    });
                })
                .catch((error) => error)
                .finally(() => setLoading(false)),
        [dispatch, resourceData?.id, resourceData?.record_type]
    );
    const resourceWrapperList: Resource_Wrapper[] | null = useSelector(
        getResourceWrapperList(resourceData?.id),
        isEqual
    );

    const mappedResourceWrapperList = useMemo(
        () =>
            resourceWrapperList?.map((resourceWrapper) => ({
                ...resourceWrapper,
                resource_type: "resource_wrapper",
                name: "resource_wrapper",
            })),
        [resourceWrapperList]
    );
    useEffect(() => {
        if (resourceData?.id) {
            fetchResourceWrappers();
        }
    }, [fetchResourceWrappers, resourceData?.id]);

    const sortedByOrderWrapperList = useMemo(
        () =>
            mappedResourceWrapperList
                ? [...mappedResourceWrapperList].sort((a, b) =>
                      a.order > b.order ? 1 : a.order < b.order ? -1 : 0
                  )
                : null,
        [mappedResourceWrapperList]
    );

    const { cards, onMoveCard } = useDragAndDropList(sortedByOrderWrapperList);

    const [defaultValues, setDefaultValues] = useState<any>(null);

    const items = useMemo(() => {
        const items = cloneDeep(cards);
        if (defaultValues) {
            (items ?? []).push(defaultValues);
        }
        return items;
    }, [cards, defaultValues]);

    const wrapperList: Wrapper[] = useSelector(getWrappers);

    const createResourceWrapper = useCallback(
        (wrapperId) => {
            const currentWrapper = wrapperList?.find((wrapper) => wrapper.id === wrapperId);
            const order = cards.length ? cards.length + 1 : 1;
            const additionalVariables = wrapperId ? { wrapper_id: wrapperId } : {};
            const currentSchema = currentWrapper?.input_schema ?? {};
            const valuesWithDefaults = getDefaultRequiredValue(currentSchema, currentSchema, null);

            setDefaultValues({
                resource_id: resourceData?.id,
                project_id: resourceData?.project_id,
                name: `${resourceData?.name} wrapper`,
                order,
                [valueAndSchema]: {
                    values: valuesWithDefaults,
                    schema: currentSchema,
                },
                wrapper_id_wrapper: {
                    name: currentWrapper?.name,
                    id: currentWrapper?.id,
                    input_schema: currentWrapper?.input_schema,
                },
                ...additionalVariables,
            });
        },
        [cards?.length, resourceData?.id, resourceData?.name, resourceData?.project_id, wrapperList]
    );

    const saveNewResourceWrapper = () => {
        fetchResourceWrappers().then(() => setDefaultValues(null));
    };

    const updateOrder = useCallback(
        ({ id, order }) => {
            return new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_UPDATE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "resource_wrapper",
                    variables: {
                        id,
                        order,
                    },
                });
            });
        },
        [dispatch]
    );

    const updateOrderAndFetch = useCallback(
        (deletedId?: string) => {
            const updateOrderPromiseList: Promise<unknown>[] = [];
            const filteredCards = cards.filter((card) => card.id !== deletedId);
            filteredCards.forEach((card, index) => {
                if (card.order !== index + 1) {
                    updateOrderPromiseList.push(updateOrder({ id: card.id, order: index + 1 }));
                }
            });

            return Promise.all(updateOrderPromiseList)
                .then((data) => {
                    if (data?.length || deletedId) {
                        fetchResourceWrappers();
                    }
                })
                .catch(console.log);
        },
        [cards, fetchResourceWrappers, updateOrder]
    );

    const deleteResourceWrapper = useCallback(
        (id) =>
            new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_DELETE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "resource_wrapper",
                    id,
                });
            })
                .then(() => updateOrderAndFetch(id))
                .catch((error) => error),
        [dispatch, updateOrderAndFetch]
    );

    const showModal = useCallback(
        (id: string, name: string) => {
            showConfirmModal({
                content: (
                    <div>{`Are you sure you want to remove the wrapper "${name}" from the ${resourceData?.record_type?.toLowerCase()} "${
                        resourceData?.name
                    }"?`}</div>
                ),
                // Return the delete promise so antd's confirm dialog keeps the
                // "Remove" button in its in-flight (spinner + disabled) state and
                // the dialog open until the removal request settles, instead of
                // closing instantly with no feedback (ui-design-principle-buttons:
                // async-triggering buttons show in-progress feedback).
                onConfirm: () => deleteResourceWrapper(id),
                confirmText: "Remove",
                cancelButtonProps: cancelButtonProps,
                confirmButtonProps: { className: "button button--danger" },
                className: "confirmationModal",
            });
        },
        [deleteResourceWrapper, resourceData?.name, resourceData?.record_type]
    );
    const isScheduleEnabled =
        resourceData?.record_type?.toLowerCase() === "schedule" &&
        !!(resourceData as Schedule)?.is_enabled;

    const isRecordDisabled = isDisabled || isReadOnly || isNewRecord || isScheduleEnabled;

    // when open a flow, resource_wrapper should be collapsed (isExpanded false)
    // when after create a new resource_wrapper, it should stay expanded (isExpanded true)
    // so on mount of ResourceWrapperContainer isExpanded should be false
    const [isExpandedInitially, setExpandedInitially] = useState(false);
    const [isInitialRender, setInitialRender] = useState(true);

    useMount(() => {
        setInitialRender(false);
    });

    useEffect(() => {
        if (cards && !isInitialRender) {
            setExpandedInitially(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cards]);

    return (
        <div className={styles.resourceWrapperContainer}>
            <AddWrapperComponent
                createResourceWrapper={createResourceWrapper}
                canAddWrapper={!isDisabled && !isNewRecord && !isScheduleEnabled}
                isDisabledWithTooltip={!!defaultValues}
                isListLoading={isLoading}
            />

            {items?.map((wrapper, index) => {
                const isNew = !wrapper?.id;
                const itemProps = isNew
                    ? {
                          isNew,
                          saveNewResourceWrapper,
                          onDelete: () => {
                              setDefaultValues(null);
                              setNestedFormSaveData?.((prevData) => {
                                  const data = { ...prevData };
                                  delete data[newRecordId];
                                  return data;
                              });
                          },
                          key: "new_resource_wrapper",
                          canDrag: false,
                      }
                    : { isExpandedInitially };
                return (
                    <ResourceWrapper
                        onMoveCard={onMoveCard}
                        index={index}
                        wrapper={wrapper}
                        updateOrderAndFetch={updateOrderAndFetch}
                        showModal={showModal}
                        key={wrapper.id}
                        isDisabled={isRecordDisabled}
                        canDrag={cards.length > 1 && !isDisabled}
                        setNestedFormSaveData={setNestedFormSaveData}
                        nestedFormSaveData={nestedFormSaveData}
                        parentResourceType={getTypedLowerCase(
                            (resourceData as Record)?.record_type
                        )}
                        parentResourceId={resourceData?.id}
                        {...itemProps}
                    />
                );
            })}
        </div>
    );
};
